import React from 'react';

import { ArrowRight } from 'lucide-react';

import { convertMRRToTimeframe } from 'common/company/RevenueHelpers';
import getIdeaURL from 'common/ideas/utils/getIdeaURL';
import styles from 'css-module/components/subdomain/admin/AdminQueue/_Suggestion.module.scss';

import AdminQueuePost from './AdminQueuePost';

import type { Board } from 'common/api/endpoints/boards';
import type { Company } from 'common/api/endpoints/companies';
import type {
  StrippedIdea,
  StrippedInsight,
  WithAuthor,
  WithUsers,
} from 'common/api/endpoints/queue';
import type { StrippedUser } from 'common/api/endpoints/users';
import type { Translation } from 'common/api/resources/i18n';

interface CommonPostSuggestionProps {
  author: StrippedUser | undefined;
  board?: Board;
  workaround?: string | null;
  created?: string;
  details: string | null;
  title: string;
  translation?: Translation<{ title: string; details: string }>;
}

const MinDetailsLength = 150;

interface UniquePostSuggestionProps extends CommonPostSuggestionProps {
  onClick: () => void;
}

export const UniquePostSuggestion = ({
  author,
  board,
  created,
  details,
  onClick,
  title,
  translation,
  workaround,
}: UniquePostSuggestionProps) => (
  <button className={styles.queueItemWrapper} onClick={onClick}>
    <AdminQueuePost
      className={styles.queueSuggestionItem}
      title={title}
      translation={translation}
      details={details}
      author={author}
      created={created}
      board={board}
      workaround={workaround}
      truncate={false}
    />
  </button>
);

interface UniqueInsightSuggestionProps {
  insight: WithUsers<WithAuthor<StrippedInsight>>;
  idea: WithAuthor<StrippedIdea>;
}

export const UniqueInsightSuggestion = ({ insight, idea }: UniqueInsightSuggestionProps) => {
  const user = insight.users[0] ?? insight.author;
  return (
    <div className={styles.duplicateQueueItem}>
      {insight.url ? (
        <a
          className={styles.queueItemWrapper}
          href={insight.url}
          target="_blank"
          rel="noopener noreferrer">
          <AdminQueuePost
            title={''}
            details={insight.value}
            author={user}
            created={insight.created}
          />
        </a>
      ) : (
        <div className={styles.queueItemWrapper}>
          <AdminQueuePost
            title={''}
            details={insight.value}
            author={user}
            created={insight.created}
          />
        </div>
      )}
      <ArrowRight className={styles.mergeButton} strokeWidth={1} width={32} />
      <a
        className={styles.queueItemWrapper}
        href={getIdeaURL(idea)}
        target="_blank"
        rel="noopener noreferrer">
        <AdminQueuePost
          title={idea.title}
          details={idea.description}
          author={idea.author}
          created={idea.created}
        />
      </a>
    </div>
  );
};

interface UniqueIdeaSuggestionProps {
  idea: WithAuthor<StrippedIdea>;
}

export const UniqueIdeaSuggestion = ({ idea }: UniqueIdeaSuggestionProps) => {
  return (
    <a
      className={styles.queueItemWrapper}
      href={getIdeaURL(idea)}
      target="_blank"
      rel="noopener noreferrer">
      <AdminQueuePost
        className={styles.queueSuggestionItem}
        title={idea.title}
        details={idea.description}
        author={idea.author}
        created={idea.created}
        truncate={false}
      />
    </a>
  );
};

interface DuplicateIdeaSuggestionProps {
  fromIdea: WithAuthor<StrippedIdea>;
  intoIdea: WithAuthor<StrippedIdea>;
}

export const DuplicateIdeaSuggestion = ({ fromIdea, intoIdea }: DuplicateIdeaSuggestionProps) => {
  return (
    <div className={styles.duplicateQueueItem}>
      <a
        className={styles.queueItemWrapper}
        href={getIdeaURL(fromIdea)}
        target="_blank"
        rel="noopener noreferrer">
        <AdminQueuePost
          title={fromIdea.title}
          details={fromIdea.description}
          author={fromIdea.author}
          created={fromIdea.created}
        />
      </a>
      <ArrowRight className={styles.mergeButton} strokeWidth={1} width={32} />
      <a
        className={styles.queueItemWrapper}
        href={getIdeaURL(intoIdea)}
        target="_blank"
        rel="noopener noreferrer">
        <AdminQueuePost
          title={intoIdea.title}
          details={intoIdea.description}
          author={intoIdea.author}
          created={intoIdea.created}
        />
      </a>
    </div>
  );
};

interface DuplicatePostSuggestionsProps extends CommonPostSuggestionProps {
  sourceType: 'draft' | 'post';
  company: Company;
  duplicatePost: CommonPostSuggestionProps & {
    score: number;
    commentCount: number;
    totalMRR: number;
  };
  onClickDuplicatePost: () => void;
  onClickOriginalPost: () => void;
}

export const DuplicatePostSuggestion = ({
  author,
  board,
  company,
  created,
  details,
  sourceType,
  title,
  translation,
  duplicatePost,
  onClickDuplicatePost,
  onClickOriginalPost,
  workaround,
}: DuplicatePostSuggestionsProps) => {
  // we only truncate the dupe details if a) the source post has details that we can match the character count to b) the source post is a draft c) the src details are longer than MinDetailsLength
  const shouldManuallyTruncateDupeDetails =
    !!details && sourceType === 'draft' && details.length > MinDetailsLength; // MinDetailsLength prevents us truncating the dupe to 10 chars, for example

  return (
    <div className={styles.duplicateQueueItem}>
      <button className={styles.queueItemWrapper} onClick={onClickDuplicatePost}>
        <AdminQueuePost
          title={title}
          translation={translation}
          details={details}
          author={author}
          created={created}
          board={board}
          truncate={sourceType === 'post'}
          workaround={workaround}
        />
      </button>
      <ArrowRight className={styles.mergeButton} strokeWidth={1} width={32} />
      <button className={styles.queueItemWrapper} onClick={onClickOriginalPost}>
        <AdminQueuePost
          title={duplicatePost.title}
          translation={duplicatePost.translation}
          details={duplicatePost.details}
          author={duplicatePost.author}
          created={duplicatePost.created}
          board={board}
          manualTruncate={shouldManuallyTruncateDupeDetails}
          meta={{
            score: duplicatePost.score,
            commentCount: duplicatePost.commentCount,
            ...(company.hasCollectedMonthlySpend && {
              monthlySpend: convertMRRToTimeframe(duplicatePost.totalMRR, company.revenueTimeframe),
            }),
          }}
          truncate={!shouldManuallyTruncateDupeDetails}
        />
      </button>
    </div>
  );
};
